import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import type { AppRouter, NestedChildren, SaveFormComponent } from '@viapeople/api'

export type RouterOutput = inferRouterOutputs<AppRouter>
export type RouterInput = inferRouterInputs<AppRouter>

export type Form = NonNullable<RouterOutput['admin']['forms']['getById']>
export type FormComponent = Omit<SaveFormComponent, 'children' | 'reportLabel'> & {
	children?: NestedChildren[]
	reportLabel: string | null
}
export type FormInput = RouterInput['admin']['forms']['save']
export type FormInputComponent = NestedChildren
export type FormData = RouterOutput['admin']['forms']['getById']
export type FormEditInput = RouterInput['admin']['forms']['save']
export type FormSearchInput = RouterInput['admin']['forms']['search']
export type FormComponentName = FormComponent['name']
export type FormComponentType = FormComponent['type']
export type CalculatedRole =
	RouterOutput['admin']['cycles']['roles']['permissions']['list']['data']['fixedRoles'][number]['fixedRole']

export type PermissionTemplate = RouterOutput['admin']['permissionTemplates']['search']['data'][number]
export type CycleExecutionTask = RouterOutput['client']['tasks']['show']

export interface ProcessedFormComponent extends Omit<FormComponent, 'reportLabel'> {
	children: ProcessedFormComponent[]
	formId: string
	evaluationId: string | null
	value?: string | number | boolean | null | Array<{ value: string; label: string }>
	optional?: boolean
	write?: boolean
	reportOnly?: boolean
	reportLabel: string | null
	createdAt?: string
}

export interface PropertyEditorProps {
	name: string
	label?: string
	value: string | number | boolean | null | Array<{ value: string; label: string }>
	onChange: (value: string | number | boolean | null | Array<{ value: string; label: string }>) => void
	options?: Array<{
		value: string
		label: string
		context?: Record<string, unknown>
	}>
	defaultValue?: string
}

export enum CycleStepType {
	CYCLE_START = 'cycle_start',
	EDIT_REVIEWERS_LIST = 'edit_rater_list',
	APPROVE_REVIEWERS_LIST = 'approve_rater_list',
	SELF_EVALUATION = 'self_evaluation',
	GROUP_EVALUATION = 'group_evaluation',
	MANAGER_EVALUATION = 'manager_evaluation',
	UNSOLICITED_EVALUATION = 'unsolicited_evaluation',
	CALIBRATION = 'calibration',
	REVIEW = 'review',
	RELEASE = 'release',
	ACKNOWLEDGE = 'acknowledge',
	EMAIL = 'email',
	HOLD = 'hold',
	CYCLE_END = 'cycle_end',
}

export type PermissionResult = Omit<
	PermissionTemplate['fixedRoles'][number],
	'fixedRole' | 'role' | 'deleted' | 'id' | 'createdAt' | 'updatedAt'
>

export type PermissionSource = PermissionTemplate['fixedRoles'][number] | PermissionTemplate['roles'][number]

export type AccessLevel = 'read' | 'write' | 'report' | 'none'

export interface JumpToItem {
	id: string
	label: string
}

export type ActionLocation = 'header' | 'signature' | 'bottom'

export interface ActionButton {
	key: string
	label: string
	location: ActionLocation
	movesForward: boolean
	variant?: 'primary' | 'secondary' | 'destructive'
	requiresSignature?: boolean
}

export interface CycleStepActions {
	headerActions: ActionButton[]
	signatureActions: ActionButton[]
	bottomActions: ActionButton[]
}

export interface EvaluateeData {
	id?: string
	firstName?: string
	lastName?: string
	externalId?: string
	directManager?: {
		id?: string
		firstName?: string
		lastName?: string
	} | null
	secondaryManager?: {
		id?: string
		firstName?: string
		lastName?: string
	} | null
	position?: string
	dateOfHire?: string
}
